import * as R from 'ramda'
import {
  GetPinnedMessages,
  IMessage,
  Message,
  PinMessages,
  reqGetPinnedMessages,
  reqPinMessages,
} from '@weblite/ts-action/message'
import { Mutex } from 'async-mutex'
import { request } from '$setup/ws'
import { isPublicHall } from '$logic/hall'
import { getRoomLastMessage } from '$logic/room-last-message'
import { getWappTheme } from '$common/wapp-bar-theme'
import {
  showMessagePinnedSnackbar,
  showMessageUnPinnedSnackbar,
} from '$comp/snackbars'
import { t } from '$i18n'
import { pinnedMessagesStorage } from '$logic/pin/storage'
import { getOpenedRoomId } from '$logic/opened-room'
import { openedRoomPinnedMessagesStorage } from '$logic/pin/opened-room-storage'
import { FETCH_MESSAGES_WINDOW_SIZE, saveMessages } from '$logic/message'
import * as messageService from '$logic/message/service'
import { pinContentApi } from '$comp/_app/components/chat/components/pin/components'

async function getRoomPinnedMessages(roomId: string) {
  const { iid } = (await getRoomLastMessage(roomId)) || {}
  if (!iid) return []

  const publicHall = await isPublicHall(roomId)
  const { payload, error } = await request<GetPinnedMessages>(
    reqGetPinnedMessages({
      roomId,
      publicHall,
      iid,
      count: FETCH_MESSAGES_WINDOW_SIZE, // TODO: check this
      position: 'PREVIOUS',
    }),
  )
  if (error) {
    console.error(error)
    return []
  }
  return payload.messages
}

export async function setPinMessageByIid(
  roomId: string,
  iids: number[],
  pinned: boolean,
  bidirectional?: boolean,
) {
  const { error } = await request<PinMessages>(
    reqPinMessages({ roomId, iids, pinned, bidirectional }),
  )
  if (error) throw new Error(error.code)

  await messageService.saveEditMessage(
    iids.map(iid => ({ roomId, iid, pinned })),
  )
}

export const getPinnedMessageDecorator = (message: Message) => {
  if (!message) return null
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { body } = message
  if (body) return body
  if (message.type === 'WAPP') {
    const { name } = getWappTheme(message.attachment.wappId)
    return name
  }
  if (
    message.type === 'AUDIO' ||
    message.type === 'VIDEO' ||
    message.type === 'FILE'
  )
    return message.attachment.filename
  if (message.type === 'IMAGE') return t('message.IMAGE')
  if (message.type === 'VOICE') return t('message.VOICE')
  if (message.type === 'CONTACT') return t('message.CONTACT')
  return null
}

export async function handleRoomPinMessage(
  roomId: string,
  iid: number,
  pinned: boolean,
  bidirectional = false,
) {
  await setPinMessageByIid(roomId, [iid], pinned, bidirectional)
  if (pinned) showMessagePinnedSnackbar()
  else showMessageUnPinnedSnackbar()
}

export async function removeRoomAllPinMessages(roomId: string) {
  const pinData = await pinnedMessagesStorage.get(roomId)
  if (pinData?.iids)
    await setPinMessageByIid(roomId, pinData.iids, false, false)
}

const mutex = new Mutex()
export async function handleEditMessages(
  edited: Pick<IMessage, 'iid' | 'roomId' | 'body' | 'attachment' | 'pinned'>[],
) {
  const messagesToEdit = R.groupBy(
    R.prop('roomId'),
    edited.filter(R.has('pinned')),
  )

  await mutex.runExclusive(async () => {
    await Promise.all(
      R.toPairs(messagesToEdit).map(([roomId, patches]) => {
        return new Promise(async res => {
          await Promise.all(
            patches.map(async patch => {
              await new Promise(async res => {
                const oldPinnedData = await pinnedMessagesStorage.get(
                  patch.roomId,
                )
                const update = R.groupBy(
                  ({ pinned }) => (pinned ? 'pinned' : 'unpinned'),
                  patches,
                )
                const iids = R.sort(
                  (a, b) => a - b,
                  R.uniq(
                    R.concat(
                      R.pluck('iid', update.pinned || []),
                      R.difference(
                        oldPinnedData?.iids || [],
                        R.pluck('iid', update.unpinned || []),
                      ),
                    ),
                  ),
                )

                if (roomId === getOpenedRoomId())
                  openedRoomPinnedMessagesStorage.replace(iids)
                pinnedMessagesStorage.replace({ roomId, iids })
                res(null)
              })
            }),
          )
          res(null)
        })
      }),
    )
  })
}

export function removeRoomPinsSync() {
  openedRoomPinnedMessagesStorage.deleteAll()
}
export async function initiateRoomPinMessages(roomId: string) {
  const { iids: pinMessages } = (await pinnedMessagesStorage.get(roomId)) || {}
  if (pinMessages) openedRoomPinnedMessagesStorage.replace(pinMessages)
  else {
    const iids = await calcRoomPinnedMessages(roomId)
    pinnedMessagesStorage.replace({ roomId, iids })
    openedRoomPinnedMessagesStorage.replace(iids)
  }
}

export async function calcRoomPinnedMessages(roomId: string) {
  const messages = await getRoomPinnedMessages(roomId)
  saveMessages(messages)
  await messageService.saveEditMessage(
    messages.map(message => ({ roomId, iid: message.iid, pinned: true })),
  )
  return R.reverse(R.pluck('iid', messages))
}

export async function getWithPinHistoryRoomIds() {
  // TODO #optimize use better algorithm b/c it's sorted OR cache?
  return R.uniq(
    R.pluck(
      0,
      await pinnedMessagesStorage.findMany(undefined, { keyOnly: true }),
    ),
  ) as string[]
}

export async function getRoomLastPinIid() {
  const roomId = await getOpenedRoomId()
  const { iids } = (await pinnedMessagesStorage.get(roomId)) || {}
  return iids?.length ? R.last(iids) : undefined
}

const viewportPinData = {
  pinList: [],
  roomId: '',
} as {
  pinList: number[]
  roomId: string
}

const viewportMessages: {
  messageList: number[]
  roomId: string
} = {
  messageList: [],
  roomId: '',
}

export const getLastPinInViewPort = () => R.last(viewportPinData.pinList)

export const isMessageVisible = (roomId: string, iid: number) => {
  return (
    viewportMessages.roomId === roomId &&
    viewportMessages.messageList.findIndex(m => m === iid)
  )
}

export const getTopViewportMessageIid = () => {
  return viewportMessages.messageList.length > 0
    ? Math.min(...viewportMessages.messageList)
    : 0
}

export function changeMessageVisibilityInViewPort(
  roomId: string,
  iid: number,
  visibility: boolean,
) {
  if (roomId !== viewportMessages.roomId) {
    viewportMessages.roomId = roomId
    viewportMessages.messageList = []
  }

  if (visibility) {
    if (!viewportMessages.messageList.includes(iid))
      viewportMessages.messageList.push(iid)
  } else {
    const index = viewportMessages.messageList.findIndex(item => item === iid)
    if (index !== -1) viewportMessages.messageList.splice(index, 1)
  }
}

export async function changePinVisibilityInViewPort(
  roomId: string,
  iid: number,
  visibility: boolean,
) {
  if (roomId !== viewportPinData.roomId) {
    viewportPinData.roomId = roomId
    viewportPinData.pinList = []
  }
  if (visibility) {
    const oldPins = viewportPinData.pinList
    if (oldPins.includes(iid)) return
    if (oldPins.length) {
      if (iid > oldPins[oldPins.length - 1]) {
        viewportPinData.pinList = R.append(iid, oldPins)
        //   append
        pinContentApi.getLast()
      } else if (iid < oldPins[0]) {
        viewportPinData.pinList = R.prepend(iid, oldPins)
        //   prepend
        // nothing()
      } else {
        const index = oldPins.findIndex(item => item > iid)
        viewportPinData.pinList = R.insert(index, iid, oldPins)
        //   ?
      }
    } else {
      viewportPinData.pinList = [iid]
      //   append
      pinContentApi.getLast()
    }
  } else {
    const index = viewportPinData.pinList.findIndex(item => item === iid)
    if (index === -1) return
    viewportPinData.pinList.splice(index, 1)
    if (index === 0 || index === viewportPinData.pinList.length - 1) {
      pinContentApi.getLast()
    }
  }
}
